import { useCallback, useEffect, useRef, useState } from 'react'
import { Plus } from 'lucide-react'

import type { VarlessCopyId } from '@npc/copy'

import { t } from '../../copy'
import type { Effort } from './model-picker'
import { formatTokens, thinkingPhaseOf, thinkingTokensOf } from './rail'
import { T } from '../../copy/text'
import { Composer } from './composer-ui'
import type { DerivedMention } from './composer'
import type { WirePlan } from './plan'
import type { DraftAttachment } from './attachments'
import { RunBlocks, trustedPlatformActionOf } from './runcards'
import type { RunStep } from './runsteps'
import { MessageAttachments } from './message-attachments'
import { TeamPanel, type InvitablePerson, type WorkItemNode } from './panels'
import { DecisionsPanel, EvidencePanel } from './sidecar-panels'
import { RunBoardPanel, WorkflowSummaryCard } from './run-panel'
import type { RailGroup } from './rail'
import { HELD_LINES, mayShowRunning, planRecovery, type GapEvent } from './recovery'
import type { ModelOption } from './model-picker'
import type { Evidence, SubFailExitKind } from './subfail'
import type { LuluMessage, LuluParticipant, LuluSkill, PendingDecision } from './queries'
import { resolveDisplayName, type HumanMember, type TeamMember } from './roster'
import type { CurrentPlan, RosterMember } from './wire'
import {
  SESSION_STATE_FACTS,
  projectSessionState,
  type LinkState,
  type Tone,
} from './session-state'
import type { LuluSnapshot } from './snapshot'
import {
  RAIL_ICON_WIDTH,
  SIDECAR_TABS,
  type ResolvedTracks,
  type SidecarMode,
  type SidecarTab,
} from './sidecar'

/**
 * Lulu 工作台的呈现层。纯 props，不取数、不路由。
 *
 * # 这一屏的结构就是它的合同
 *
 * 三条轨道并列在同一根 flex 主轴上：会话栏 | 聊天列 | Sidecar。
 *
 * **Sidecar 是主轴上的第三个元素，不是浮在上面的层。** 这一条不是审美：
 * 一个 `position: fixed` 的 Sidecar 没法「让位」，它只能盖。而设计稿把
 * 「不覆盖消息、输入框、滚动条」单独标红，因为上一版就是覆盖式的，
 * 于是每次看工作板都要把聊天挡掉。
 *
 * 用例 `view.test.tsx` 里有一条盯着它：Sidecar 与聊天列必须是**兄弟节点**。
 * 那条用例挡的就是「有人为了省事把它改回 fixed」。
 */

const TONE_TEXT: Record<Tone, string> = {
  n: 'text-npc-lulu-t2',
  g: 'text-npc-lulu-ok',
  w: 'text-npc-lulu-warn',
  r: 'text-npc-lulu-bad',
  a: 'text-npc-lulu-t1',
}

const TONE_DOT: Record<Tone, string> = {
  n: 'bg-npc-lulu-t4',
  g: 'bg-npc-lulu-ok',
  w: 'bg-npc-lulu-warn',
  r: 'bg-npc-lulu-bad',
  a: 'bg-npc-lulu-t2',
}

/** 空态那四条起手式。顺序是设计稿 W01 的顺序，不是随便排的。 */
const EMPTY_STARTERS: readonly (readonly [VarlessCopyId, VarlessCopyId])[] = [
  ['lulu_workbench.empty.s1_t', 'lulu_workbench.empty.s1_d'],
  ['lulu_workbench.empty.s2_t', 'lulu_workbench.empty.s2_d'],
  ['lulu_workbench.empty.s3_t', 'lulu_workbench.empty.s3_d'],
  ['lulu_workbench.empty.s4_t', 'lulu_workbench.empty.s4_d'],
]

const SIDECAR_TAB_COPY = {
  team: 'lulu_workbench.sidecar.tab_team',
  board: 'lulu_workbench.sidecar.tab_board',
  decisions: 'lulu_workbench.sidecar.tab_decisions',
  evidence: 'lulu_workbench.sidecar.tab_evidence',
} as const

export interface LuluViewProps {
  readonly snapshot: LuluSnapshot
  /**
   * 团队成员。**`null` 表示读不到，`[]` 表示真的一位都没有。**
   *
   * 这两件事在界面上说的话完全不同：读不到该写「还没接上」，
   * 真的没有才写「空团队不是错误」。合成一个空数组，一次读不到就会被
   * 显示成「你的团队是空的」——与项目列表那边「读不到 ≠ 没有」是同一条
   * 规则，只是换了个对象。
   */
  /** `undefined` = 正在读，`null` = 读不到，`[]` = 真的没有。三态不许合并。 */
  /** 队里的人。来自 workboard 的 roster；三态不许合并。 */
  /** 有新消息的会话 id（L-67，客户端版）。 */
  readonly unread?: ReadonlySet<string>
  readonly team: readonly RosterMember[] | null | undefined
  /** 工作项。同上，`null` 是读不到。 */
  /** 同 team：`undefined` 正在读 / `null` 读不到 / `[]` 真的空。 */
  readonly board: readonly WorkItemNode[] | null | undefined
  /** 待决定。`null` = 读不到；`[]` = 真的没有要你决定的事。 */
  /** `undefined` 正在读 / `null` 读不到 / `[]` 真的没有要你决定的事。 */
  readonly decisions: readonly PendingDecision[] | null | undefined
  /** 会话的对话模型。`null` = 读不到，界面据此不画选择器的当前值。 */
  readonly composerModel: {
    readonly policy: 'auto' | 'fixed'
    readonly profile_id?: string
    readonly model_id?: string
  } | null
  /** 真发一条消息。不给就发不出去（按钮 disabled），**不假装发出去了**。 */
  readonly onSendMessage?: (
    body: string,
    attachmentIDs: readonly string[],
    effort: Effort | null,
    planOnly: boolean,
    /** 他在这句话里 @ 到的那些人。空数组 = 没点名，这一轮归 Lulu。 */
    mentions: readonly DerivedMention[],
  ) => Promise<void>
  /** 真回答一个红级决定。同上，不给就点不动。 */
  readonly onAnswerDecision?: (decisionId: string, approve: boolean) => Promise<void>
  /** 子 Agent 三条出路。不给则三颗按钮点不动。 */
  /** 撤回这次安排。不给的话板上不出现撤回按钮。 */
  readonly onUndoTask?: (taskId: string) => Promise<void>
  readonly onRecoverSubrun?: (
    subrunId: string,
    kind: SubFailExitKind,
    adapter?: string,
  ) => Promise<void>
  /** 平台权威取消一个临时子 Agent。父 Agent 和兄弟运行不受影响。 */
  readonly onCancelSubrun?: (subrunId: string) => Promise<void>
  /**
   * 停下这一轮。**与上面那条停的不是同一个东西**：那条停一个子 Agent，
   * 这条把这一轮里还没做完的活一个不剩地收掉。不给就不出现那颗按钮。
   */
  readonly onStopRound?: (scope: 'all' | 'turn') => Promise<{ readonly cancelledItems: number }>
  /** 这条话头暂停着没有（agent_conversations.paused_at）。 */
  readonly paused?: boolean
  /** 暂停 / 继续这条话头（POST …/pause、…/resume）。不给就不出现那颗按钮。 */
  readonly onPause?: () => Promise<void>
  readonly onResume?: () => Promise<void>
  /** GET /skills 的投影，给输入框那份「/」清单挑技能。`null` 读不到 / `undefined` 还没读。 */
  readonly skills?: readonly LuluSkill[] | null
  /** 真改名（PATCH）。不给，「/」清单里就没有「改名」那一条。 */
  readonly onRenameConversation?: (title: string) => Promise<void>
  /** 可选模型列表与真持久化。 */
  readonly models?: readonly ModelOption[] | null
  readonly onPickModel?: (policy: 'auto' | 'fixed', modelId: string | undefined) => Promise<void>
  readonly railGroups: readonly RailGroup[]
  /** 正在等 Lulu 接话已经多少秒；`null` = 没在等。 */
  readonly thinkingSeconds: number | null
  readonly messages: readonly LuluMessage[]
  readonly participants: ReadonlyMap<string, LuluParticipant>
  readonly tracks: ResolvedTracks
  readonly sidecarTab: SidecarTab | null
  readonly onOpenSidecar: (tab: SidecarTab) => void
  readonly onCloseSidecar: () => void
  readonly onSelectConversation: (id: string) => void
  readonly onNewConversation: () => void
  /** 侧栏读不到时的重试。不给就不渲染那颗按钮。 */
  readonly onRetrySidecar?: () => void
  /** 绑定卡能选的项目。`null` = 读不到。 */
  /** 库里还没进这条会话的那几位。 */
  readonly teamCandidates?: readonly TeamMember[]
  /** 一起管这条会话的真人。与 team 分开，见 TeamPanelProps.people 那段。 */
  readonly people?: readonly HumanMember[]
  /** 「一起管」里还没进这条会话的人（L-58）。 */
  readonly invitablePeople?: readonly InvitablePerson[]
  readonly onInvitePerson?: (userId: string) => Promise<void>
  readonly onRemovePerson?: (userId: string) => Promise<void>
  /** 把一位候选加进这条会话。不给就不渲染那颗按钮。 */
  readonly onJoinTeam?: (agentProfileId: string) => Promise<void>
  /**
   * 新会话启动器。给了就**替掉中间那一栏**，左侧会话栏照常渲染、
   * 右侧 Sidecar 不渲染（画板 Z01：「主聊天区换成启动页……不占右侧 Sidecar」）。
   */
  readonly launcher?: React.ReactNode
  /**
   * 当前那一版计划。`undefined` = 还在读，`null` = 真的还没有计划。
   * 两者不许合并：还在读时什么都不说，真的没有时要说「还没有计划」。
   */
  readonly plan?: CurrentPlan | null
  readonly bindTargets?: readonly { readonly id: string; readonly name: string }[] | null
  /** 真绑一个项目。不给就不渲染那几颗。 */
  readonly onBindProject?: (projectId: string) => Promise<void>
  readonly replyAskedForProject?: boolean
  /** 「开始做」那张卡要的四样。四层都要接住并传下去——
      少接一层，下面收到的是 undefined，而那时卡整块不画、
      屏幕上一点异常都看不出来（runsteps 那条链就是这么断了两截的）。 */
  readonly goalPlan?: WirePlan | null
  /** 刚点完「开始做」拿到的 task_id。见 plan.ts 那段。 */
  readonly justScheduledTaskId?: string | null
  /** 会话附件那一整套。不给 = 这一屏不支持附件，那一排整个不画。 */
  readonly attachments?: readonly DraftAttachment[]
  readonly onPickAttachment?: () => void
  readonly onRetryAttachment?: (localId: string) => void
  readonly onRemoveAttachment?: (localId: string) => void
  readonly attachmentNotice?: 'too_large' | 'bad_type' | 'too_many' | null
  readonly onStartWork?: () => void
  readonly startingWork?: boolean
  readonly startWorkFailed?: boolean
  /**
   * 这条会话上收到的真实过程步骤（SSE 的 ledger 事件攒的）。
   *
   * **2026-08-30 补的**：在此之前这一层根本没有这个 prop，
   * 于是 ChatColumn 声明并转发得好好的、`RunBlocks` 也接得好好的，
   * 而 `liveSteps` 恒为 undefined —— 屏幕上是一条**空的**「工作过程」横条，
   * 一步都没有。而单测全绿、后端全绿、check-shared-consumers 全绿。
   *
   * 「抽出一个共享的东西，不等于用上了它」——这一晚第三次。
   * 前两次是 humanAskedForWork 与 jobseam，这一次是我自己在提交信息里
   * 宣称接通了，而中间断了两截（这一层没有 prop、index.tsx 没有传）。
   */
  readonly runSteps?: readonly RunStep[]
  readonly onResizeSidecar: (width: number) => void
  /** 会话导航已由全局外壳接管；不再渲染本屏第二套栏或补位按钮。 */
  readonly conversationNavigationExternal?: boolean
}

export function LuluView({
  snapshot,
  unread,
  team,
  decisions,
  composerModel,
  onSendMessage,
  onAnswerDecision,
  onRecoverSubrun,
  onUndoTask,
  onCancelSubrun,
  onStopRound,
  paused,
  onPause,
  onResume,
  skills,
  onRenameConversation,
  models,
  onPickModel,
  board,
  railGroups,
  thinkingSeconds,
  messages,
  participants,
  runSteps,
  tracks,
  sidecarTab,
  onOpenSidecar,
  onCloseSidecar,
  onSelectConversation,
  onNewConversation,
  onRetrySidecar,
  teamCandidates,
  people,
  invitablePeople,
  onInvitePerson,
  onRemovePerson,
  onJoinTeam,
  plan,
  launcher,
  bindTargets,
  onBindProject,
  replyAskedForProject,
  goalPlan,
  justScheduledTaskId,
  attachments,
  onPickAttachment,
  onRetryAttachment,
  onRemoveAttachment,
  attachmentNotice,
  onStartWork,
  startingWork,
  startWorkFailed,
  onResizeSidecar,
  conversationNavigationExternal = false,
}: LuluViewProps) {
  // 「此刻」在这里取一次往下传，**不在 DecisionsPanel 里 new Date()**：
  // 组件里现取的话，用例没法把时间挪到过期之后，于是「过期要重新问」
  // 那条就只能靠读代码相信它。
  const now = new Date()
  // 点了起手式之后要填进输入框的那句话。
  // 不走 sessionStorage 直写 + 自定义事件：那条路是隐式的，
  // 而这里只需要一个从上往下的普通 prop。
  const [starterSeed, setStarterSeed] = useState('')
  const state = projectSessionState(snapshot.sessionState, snapshot.link)
  const facts = SESSION_STATE_FACTS[state]

  return (
    <div
      data-testid="lulu-root"
      className="lulu-theme flex min-h-0 w-full flex-1 bg-npc-lulu-bg-0 text-npc-lulu-t2"
    >
      {!conversationNavigationExternal && tracks.railMode !== 'overlay' ? (
        <ConversationRail
          groups={railGroups}
          activeId={snapshot.conversationId}
          width={tracks.railWidth}
          icons={tracks.railMode === 'icons'}
          onSelect={onSelectConversation}
          unread={unread}
          onNew={onNewConversation}
        />
      ) : null}

      {/*
        启动器**只换中间这一栏**。画板 Z01 顶上那句话是合同：
        「点「+ 新会话」后**主聊天区**换成启动页，不弹覆盖层、不盖旧聊天、
        不占右侧 Sidecar」。

        上一版在 index.tsx 里 `if (launching) return <LuluFrame>…`——
        **提前返回，把整个框都换掉了**，于是左边 232px 的会话栏整条消失。
        arron 对着设计图看了三轮才把它指出来，而我前两轮都在查缓存：
        代码确实是新的、缓存也确实没问题，缺的是一整栏。

        Sidecar 在这一档不渲染，同样来自那句话的后半段。
      */}
      {launcher ?? (
        <ChatColumn
          snapshot={snapshot}
          messages={messages}
          participants={participants}
          team={team}
          board={board}
          thinkingSeconds={thinkingSeconds}
          runSteps={runSteps}
          onStopRound={onStopRound}
          skills={skills}
          onRenameConversation={onRenameConversation}
          railHidden={!conversationNavigationExternal && tracks.railMode === 'overlay'}
          isMobile={tracks.mobile}
          onNewConversation={onNewConversation}
          onPickStarter={setStarterSeed}
          starterSeed={starterSeed}
          bindTargets={bindTargets}
          onBindProject={onBindProject}
          replyAskedForProject={replyAskedForProject}
          goalPlan={goalPlan}
          justScheduledTaskId={justScheduledTaskId}
          attachments={attachments}
          onPickAttachment={onPickAttachment}
          onRetryAttachment={onRetryAttachment}
          onRemoveAttachment={onRemoveAttachment}
          attachmentNotice={attachmentNotice}
          onStartWork={onStartWork}
          startingWork={startingWork}
          startWorkFailed={startWorkFailed}
          composerModel={composerModel}
          onSendMessage={onSendMessage}
          // @ 面板要列得出 Agent 库里还没进这条会话的人，并且点一下能把他叫进来。
          // 这两样 TeamPanel 早就在用，这里是第二个消费方——**不另拉一份数据**，
          // 两份「库里还有谁」迟早分家。
          teamCandidates={teamCandidates}
          people={people}
          invitablePeople={invitablePeople}
          onInvitePerson={onInvitePerson}
          onJoinTeam={onJoinTeam}
          models={models}
          onPickModel={onPickModel}
          onOpenSidecar={onOpenSidecar}
          // 红线卡进消息（修复任务闭环 §A）：与 Sidecar「要你决定」同一份数据、同一个动作。
          decisions={decisions}
          onAnswerDecision={onAnswerDecision}
        />
      )}

      {launcher === undefined && sidecarTab ? (
        <Sidecar
          decisions={decisions}
          now={now}
          onRetry={onRetrySidecar}
          teamCandidates={teamCandidates}
          people={people}
          invitablePeople={invitablePeople}
          onInvitePerson={onInvitePerson}
          onRemovePerson={onRemovePerson}
          onJoinTeam={onJoinTeam}
          plan={plan}
          onAnswerDecision={onAnswerDecision}
          onRecoverSubrun={onRecoverSubrun}
          onUndoTask={onUndoTask}
          onCancelSubrun={onCancelSubrun}
          onStopRound={onStopRound}
          paused={paused}
          onPause={onPause}
          onResume={onResume}
          models={models ?? null}
          tab={sidecarTab}
          team={team}
          board={board}
          bound={snapshot.projectId !== null}
          link={snapshot.link}
          mode={tracks.sidecarMode}
          width={tracks.sidecarWidth}
          onSelectTab={onOpenSidecar}
          onClose={onCloseSidecar}
          onResize={onResizeSidecar}
        />
      ) : null}

      {/* 状态一行，读屏软件用。视觉上它在标题栏里，但那一份是分段渲染的， */}
      {/* 读屏读起来是碎的——这里给一句完整的。 */}
      <p className="sr-only" data-testid="lulu-session-state" data-state={state}>
        <T id={facts.copyId} />
      </p>
    </div>
  )
}

function ConversationRail({
  groups,
  activeId,
  width,
  icons,
  onSelect,
  unread,
  onNew,
}: {
  groups: readonly RailGroup[]
  activeId: string | null
  width: number
  icons: boolean
  onSelect: (id: string) => void
  unread?: ReadonlySet<string>
  onNew: () => void
}) {
  const newConversationLabel = t('lulu_workbench.rail.new_conversation')
  /**
   * 把当前这条滚进视野。
   *
   * # 为什么需要它
   *
   * 选中态的样式一直是有的，而 arron 2026-08-29 看不见它——因为
   * **当前那条根本不在可视范围里**：他在一条项目会话里，而项目分组
   * 排在十几条不挂项目的会话下面，被折在下方了。
   *
   * 一个「有选中态但你看不见」与「没有选中态」在屏幕上是一回事。
   *
   * `block: 'nearest'` 是刻意的：已经在视野里的时候**一动不动**。
   * 用 'center' 的话每切一次会话整栏都跳一下，而那比不滚更烦。
   */
  const activeRef = useRef<HTMLButtonElement | null>(null)
  useEffect(() => {
    // **`?.` 两处都要。** jsdom 里没有 scrollIntoView，而这一段跑在
    // 每次切会话时——少一个问号，所有渲染这一屏的用例当场全红，
    // 而那个红与「布局坏了」长得一模一样（第一版就是这么红的）。
    activeRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [activeId])
  return (
    <nav
      data-testid="lulu-rail"
      data-mode={icons ? 'icons' : 'full'}
      style={{ width, flex: `0 0 ${width}px` }}
      className="flex min-h-0 flex-col border-r border-npc-lulu-line-1 bg-npc-lulu-bg-1"
    >
      <div className="shrink-0 p-2">
        <button
          type="button"
          data-testid="lulu-new-conversation"
          onClick={onNew}
          aria-label={newConversationLabel}
          title={newConversationLabel}
          className="flex h-9 w-full items-center justify-center gap-2 rounded-npc-button bg-npc-lulu-t1 px-3 text-npc-body-sm font-medium text-npc-lulu-bg-0 hover:opacity-90"
        >
          <Plus aria-hidden="true" className="size-4 shrink-0" />
          {icons ? null : <span>{newConversationLabel}</span>}
        </button>
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto p-2">
        {groups.map((group) => (
          <li key={group.projectId ?? '__general__'}>
            {/*
              分组标题。**图标态不显示**——那时候整栏只有 6px 宽的状态点，
              一行项目名会把它撑开，而图标态存在的理由就是不占地方。
            */}
            {icons ? null : (
              <p
                data-testid="lulu-rail-group"
                className="truncate px-2 pb-1 pt-3 text-npc-meta text-npc-lulu-t3 first:pt-1"
              >
                {group.name}
              </p>
            )}
            <ul>
              {group.conversations.map((conversation) => {
                const active = conversation.id === activeId
                return (
                  <li key={conversation.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(conversation.id)}
                      aria-current={active ? 'true' : undefined}
                      data-testid="lulu-rail-item"
                      data-active={active ? 'true' : undefined}
                      ref={active ? activeRef : undefined}
                      title={conversation.title}
                      className={[
                        'flex w-full items-start gap-2 rounded-npc-chip px-2 py-2 text-left',
                        active ? 'bg-npc-lulu-bg-3 text-npc-lulu-t1' : 'text-npc-lulu-t2',
                      ].join(' ')}
                    >
                      {/* 设计稿那颗状态点。有新消息时换成主绿——**复用它，不另加一个角标**。 */}
                      <span
                        aria-hidden="true"
                        data-testid="lulu-rail-dot"
                        data-unread={unread?.has(conversation.id) ? 'true' : undefined}
                        className={[
                          'mt-[5px] size-[6px] shrink-0 rounded-full', /* npc-token-allow: 设计稿状态点 6px，整站 token 无此档 */
                          unread?.has(conversation.id) ? 'bg-npc-green' : 'bg-npc-lulu-t4',
                        ].join(' ')}
                      />
                      {icons ? null : (
                        <span className="min-w-0 flex-1 truncate text-npc-body-sm">
                          {conversation.title}
                        </span>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          </li>
        ))}
      </ul>
    </nav>
  )
}

/**
 * 单 Agent 会话里那位的名字。
 *
 * 团队会话回 `null`（那一侧走实线形态，不需要名字）；读不到成员、
 * 或者成员不止一位、或者名字解不出来，一律回 `null`——
 * **虚线那一条上写的是一个具体的人，写错了比不写糟得多。**
 */
function soloAgentNameOf(
  mode: 'solo' | 'team' | null,
  team: readonly TeamMember[] | null,
): string | null {
  if (mode !== 'solo' || team === null || team.length !== 1) return null
  const only = team[0]
  return only ? resolveDisplayName(only) : null
}

/**
 * 中间那一栏：消息流 + 输入框 + 「开始做」那张卡 + 绑定卡。
 *
 * # 为什么它是导出的
 *
 * 设计稿 pjChat 的原话：「这是简版，**不是第二套聊天**……和公共 Lulu 是
 * 同一个内核，只是把项目固定住了」。而项目详情下那一屏原来是自己写的一份
 * （走 `screens/chat/queries` 那套查询层，2026-09-02 连屏一起删了）——**恰好是设计稿明确
 * 要避免的东西**。
 *
 * 让它复用同一个内核只有两种做法：把这一栏导出去，或者在那边再抄一遍。
 * 抄一遍的代价不是多写几百行，是**从此每加一个能力都要加两遍**，
 * 而漏掉一遍不会有任何东西报错（「开始做」那张卡就是这么只在一边有的）。
 *
 * 它是纯展示的：所有数据与动作都从 props 来，自己不发任何请求、
 * 不读路由。所以嵌进项目那一屏不需要给它任何特殊照顾。
 */
export function ChatColumn({
  snapshot,
  messages,
  participants,
  team,
  board,
  thinkingSeconds,
  railHidden,
  isMobile,
  onNewConversation,
  skills,
  onRenameConversation,
  onPickStarter,
  decisions,
  onAnswerDecision,
  starterSeed,
  bindTargets,
  onBindProject,
  replyAskedForProject,
  goalPlan,
  justScheduledTaskId,
  attachments,
  onPickAttachment,
  onRetryAttachment,
  onRemoveAttachment,
  attachmentNotice,
  onStartWork,
  startingWork,
  startWorkFailed,
  composerModel,
  gapEvents,
  onSendMessage,
  models,
  onPickModel,
  teamCandidates,
  people,
  invitablePeople,
  onInvitePerson,
  onJoinTeam,
  onOpenSidecar,
  runSteps,
  onStopRound,
}: {
  snapshot: LuluSnapshot
  messages: readonly LuluMessage[]
  participants: ReadonlyMap<string, LuluParticipant>
  team: readonly RosterMember[] | null | undefined
  /** `undefined` 正在读 / `null` 读不到 / `[]` 真的空。 */
  board: readonly WorkItemNode[] | null | undefined
  /**
   * 这条会话上收到的真实过程步骤（从 SSE 的 ledger 事件攒的）。
   *
   * **可空**：没接流时过程卡显示 block 自带的收口摘要，
   * 两者都没有就什么都不画——不补一个「正在处理」。
   */
  runSteps?: readonly RunStep[]
  /** 「停下」：叫停正在想的这一轮（走 cancel 那条）。不给就不渲染那颗按钮。 */
  onStopRound?: (scope: 'all' | 'turn') => Promise<{ readonly cancelledItems: number }>
  /**
   * 正在等 Lulu 接话已经多少秒；`null` = 没在等。
   *
   * **是秒数不是布尔**：他要知道的是「它到底有没有在动」，
   * 一个不带数字的「正在想」在第 30 秒和第 1 秒长得一模一样。
   */
  thinkingSeconds: number | null
  /** 会话栏此刻不在屏上（手机那一档）。真时标题栏补一个新会话入口。 */
  railHidden: boolean
  /**
   * 这一屏是不是手机档。
   *
   * **与 railHidden 是两件事**：railHidden 问「会话栏在不在这一屏里」，
   * 这个问「屏够不够窄」。2026-09-07 之前两者是同一个表达式，而
   * `cc1d45c93` 把会话导航搬进全局侧栏之后它恒为 false——手机上的
   * 底部 sheet、窄屏脚注、力度 sheet 一起静默失效，没有任何东西会红。
   */
  isMobile: boolean
  onNewConversation: () => void
  /** 给输入框那份「/」清单：技能与改名。见 LuluViewProps 上同名两条。 */
  skills?: readonly LuluSkill[] | null
  onRenameConversation?: (title: string) => Promise<void>
  onPickStarter?: (text: string) => void
  decisions?: readonly PendingDecision[] | null
  onAnswerDecision?: (decisionId: string, approve: boolean) => Promise<void>
  /** 点了起手式之后要填进输入框的那句话。 */
  starterSeed?: string
  bindTargets?: readonly { readonly id: string; readonly name: string }[] | null
  onBindProject?: (projectId: string) => Promise<void>
  replyAskedForProject?: boolean
  goalPlan?: WirePlan | null
  justScheduledTaskId?: string | null
  attachments?: readonly DraftAttachment[]
  onPickAttachment?: () => void
  onRetryAttachment?: (localId: string) => void
  onRemoveAttachment?: (localId: string) => void
  attachmentNotice?: 'too_large' | 'bad_type' | 'too_many' | null
  onStartWork?: () => void
  startingWork?: boolean
  startWorkFailed?: boolean
  composerModel: LuluViewProps['composerModel']
  /** 断线期间的事件。`undefined` = 没有补齐信息，界面不列具体条目。 */
  gapEvents?: readonly GapEvent[]
  onSendMessage?: (
    body: string,
    attachmentIDs: readonly string[],
    effort: Effort | null,
    planOnly: boolean,
    mentions: readonly DerivedMention[],
  ) => Promise<void>
  models?: readonly ModelOption[] | null
  onPickModel?: (policy: 'auto' | 'fixed', modelId: string | undefined) => Promise<void>
  /** Agent 库里还没进这条会话的那些；@ 面板要列它们。 */
  teamCandidates?: readonly TeamMember[]
  people?: readonly HumanMember[]
  invitablePeople?: readonly InvitablePerson[]
  onInvitePerson?: (userId: string) => Promise<void>
  /** 真把一位叫进这条会话。@ 面板与右栏团队那一格是同两个消费方。 */
  onJoinTeam?: (agentProfileId: string) => Promise<void>
  onOpenSidecar: (tab: SidecarTab) => void
}) {
  const state = projectSessionState(snapshot.sessionState, snapshot.link)
  const facts = SESSION_STATE_FACTS[state]
  // 断线期间发生了什么。事件由调用方给，**这里不编**——
  // 空数组时 planRecovery 回 count:0，界面就只说「一次补齐」，
  // 不列任何一条具体的事。编一条出来才是最坏的。
  const recovery = planRecovery(gapEvents ?? [])
  const general = snapshot.projectId === null

  return (
    <section
      data-testid="lulu-chat"
      className="flex min-h-0 min-w-0 flex-1 flex-col bg-npc-lulu-bg-0"
    >
      <header
        data-testid="lulu-title-bar"
        className="flex shrink-0 items-center gap-[9px] border-b border-npc-lulu-line-1 bg-npc-lulu-bg-0 px-4 py-[18px] pb-2" /* npc-token-allow: after-v4-taste topbar gap 9px / pad */
      >
        <h1
          data-testid="lulu-title"
          className="truncate text-[14px] font-semibold tracking-[-0.01em] text-npc-lulu-t2" /* npc-token-allow: after-v4-taste topbar title 14/600 */
        >
          {general ? <T id="lulu_workbench.title.general" /> : snapshot.title}
        </h1>
        <span
          data-testid="lulu-subtitle"
          className={[
            'shrink-0',
            general ? 'lu-pill-amber' : 'text-npc-caption text-npc-lulu-t3',
          ].join(' ')}
        >
          {general ? (
            <T id="lulu_workbench.title.no_project" />
          ) : (
            <T id="lulu_workbench.title.bound" />
          )}
        </span>

        <span className="flex-1" />

        {/*
          手机那一档会话栏整个不渲染（railMode === 'overlay'），
          于是「新会话」在手机上根本没有入口——用户进得来、开不了新的。

          所以 rail 不在时，标题栏补一颗图标按钮。只放图标不放字：
          设计稿把手机这一档定成「整屏对话，省下的 74px 全给对话」，
          在标题栏加一个词会把标题挤没。aria-label 带着那句话，
          读屏和自动化都拿得到。
        */}
        {railHidden ? (
          <button
            type="button"
            data-testid="lulu-new-conversation"
            onClick={onNewConversation}
            aria-label={t('lulu_workbench.rail.new_conversation')}
            title={t('lulu_workbench.rail.new_conversation')}
            className="flex size-9 shrink-0 items-center justify-center rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 text-npc-lulu-t2"
          >
            <Plus aria-hidden="true" className="size-4" />
          </button>
        ) : null}

        {/* 团队入口。**单 Agent 会话不渲染它** —— 设计稿写的是「不是禁用，是不存在」。 */}
        {/* 拿不到 mode 时同样不渲染：猜错的那一侧会凭空多出一个按钮，而这个 */}
        {/* 按钮的存在本身就是一句话「这条会话有团队」。 */}
        {snapshot.mode === 'team' ? (
          <button
            type="button"
            data-testid="lulu-team-entry"
            onClick={() => onOpenSidecar('team')}
            className="shrink-0 rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-2.5 py-1 text-npc-caption text-npc-lulu-t2"
          >
            <T id="lulu_workbench.sidecar.tab_team" />
          </button>
        ) : null}

        <button
          type="button"
          data-testid="lulu-board-entry"
          onClick={() => onOpenSidecar('board')}
          className="lu-btn-ghost shrink-0"
        >
          <T id="lulu_workbench.sidecar.tab_board" />
        </button>
      </header>

      <div
        data-testid="lulu-state-line"
        className="flex shrink-0 items-center gap-2 bg-npc-lulu-bg-0 px-4 pb-1 pt-0"
      >
        <span
          aria-hidden="true"
          data-testid="lulu-state-dot"
          data-animate={facts.mayAnimate ? 'yes' : 'no'}
          className={[
            'size-[7px] shrink-0 rounded-full' /* npc-token-allow: 设计稿状态点 7px，整站 token 无此档 */,
            TONE_DOT[facts.tone],
            // 只有允许动画的档才呼吸。禁止档下这个类不出现——
            // 「界面在动」是一句关于此刻的断言。
            facts.mayAnimate ? 'animate-npc-pulse' : '',
          ].join(' ')}
        />
        <span className={['text-npc-caption', TONE_TEXT[facts.tone]].join(' ')}>
          <T id={facts.copyId} />
        </span>
      </div>

      {/* W12 平台故障：责任先说清、线上状态进前三行、三条幂等承诺。
          四句的顺序由 HELD_LINES 定，**不在这里手写顺序**——
          「线上没受影响必须在前三行」那条有一个纯函数守着。 */}
      {state === 'platform_held' ? (
        <div
          data-testid="lulu-held"
          className="shrink-0 border-b border-npc-lulu-line-1 bg-npc-lulu-bg-2 px-4 py-2"
        >
          {HELD_LINES.map((id) => (
            <p key={id} className="text-npc-meta text-npc-lulu-t2">
              <T id={id} />
            </p>
          ))}
        </div>
      ) : null}

      {/* W13 恢复：具体说断线期间发生了什么，**不是「正在同步」**。
          补齐完成前不显示任何「进行中」——mayShowRunning 管那一条。 */}
      {state === 'recovering' ? (
        <div
          data-testid="lulu-recover"
          data-may-show-running={mayShowRunning(state, false) ? 'yes' : 'no'}
          className="shrink-0 border-b border-npc-lulu-line-1 bg-npc-lulu-bg-2 px-4 py-2"
        >
          <p className="text-npc-caption text-npc-lulu-t2">
            <T id="lulu_workbench.recover.headline" />
          </p>
          <p className="text-npc-meta text-npc-lulu-t3">
            <T id="lulu_workbench.recover.catching_up" vars={{ count: String(recovery.count) }} />
          </p>
          {recovery.lines.map((line, i) => (
            <p key={i} data-testid="lulu-recover-line" className="text-npc-meta text-npc-lulu-t4">
              {line.copyId === 'lulu_workbench.recover.what_happened' ? (
                <T id={line.copyId} vars={line.vars} />
              ) : (
                <T id={line.copyId} vars={line.vars} />
              )}
            </p>
          ))}
          <p className="text-npc-meta text-npc-lulu-t4">
            <T id="lulu_workbench.recover.nothing_running" />
          </p>
        </div>
      ) : null}

      <MessageList
        messages={messages}
        participants={participants}
        board={board}
        thinkingSeconds={thinkingSeconds}
        onPickStarter={onPickStarter}
        decisions={decisions}
        onAnswerDecision={onAnswerDecision}
        link={snapshot.link}
        onOpenRun={() => onOpenSidecar('board')}
        runSteps={runSteps}
        onStopRound={onStopRound}
        conversationId={snapshot.conversationId ?? undefined}
      />

      <footer className="shrink-0 bg-npc-lulu-bg-0 px-3 pb-8 pt-[22px] max-[720px]:px-3">
        <Composer
          conversationId={snapshot.conversationId}
          projectId={snapshot.projectId}
          mode={snapshot.mode}
          composerModel={composerModel}
          members={team ?? null}
          // 库里还没进这条会话的那些，以及把人叫进来的那条路。
          // **与 members 分开传**：合成一份的话，一个还没进来的候选会被
          // 当成在席成员算进人数里（TeamPanel 那侧同一条理由）。
          joinable={teamCandidates}
          people={people}
          invitablePeople={invitablePeople}
          onInvitePerson={onInvitePerson}
          onJoinTeam={onJoinTeam}
          soloAgentName={soloAgentNameOf(snapshot.mode, team ?? null)}
          onOpenSidecar={onOpenSidecar}
          onChangeAgent={() => onOpenSidecar('team')}
          onSend={onSendMessage}
          models={models}
          onPickModel={onPickModel}
          seed={starterSeed}
          asSheet={isMobile}
          link={snapshot.link}
          messageCount={messages.length}
          bindTargets={bindTargets}
          onBindProject={onBindProject}
          replyAskedForProject={replyAskedForProject}
          plan={goalPlan}
          justScheduledTaskId={justScheduledTaskId}
          attachments={attachments}
          onPickAttachment={onPickAttachment}
          onRetryAttachment={onRetryAttachment}
          onRemoveAttachment={onRemoveAttachment}
          attachmentNotice={attachmentNotice}
          // 归档的会话不接受写：这张卡整块不画。archived 从 snapshot 取，
          // 不另立一个 prop —— 那会有第二份「这条会话开着没有」的答案，
          // 而两份迟早分家。
          archived={snapshot.archived}
          onStartWork={onStartWork}
          startingWork={startingWork}
          startWorkFailed={startWorkFailed}
          skills={skills}
          onNewConversation={onNewConversation}
          onRename={onRenameConversation}
          // Esc 与等回话那一行的「停下」是同一件事，所以条件也是同一个：
          // 真有一轮在跑（thinkingSeconds 非 null）且有那条 cancel 可调。
          onStop={
            thinkingSeconds === null || !onStopRound
              ? undefined
              : () => {
                  void onStopRound('turn').catch(() => undefined)
                }
          }
        />
        {/*
          这里原来常驻一条「这一块还没接上后端 · 工作板」。

          **工作板早就接上了**（GET /conversations/{id}/workboard 是真端点），
          所以那句话从「诚实的缺口声明」变成了一句挂在输入框底下的假话。
          而它比一般的假话更贵：用户每次打字都看见它，于是学会把
          「还没接上」当成常驻装饰——等到真有东西没接上那天，没人会看。

          读不到的那一态由侧栏那句「暂时读不到 · {什么}」负责，
          它带重试、而且只在真的读失败时出现。这里不再重复说一遍。
        */}
      </footer>
    </section>
  )
}

/**
 * 一条消息该显示谁说的、用什么模型、这句归属有多可信。
 *
 * 三层顺序是硬规则（设计稿 AgentRow）：名字 → 角色和当前工作 → 模型。
 * 模型是**第三层的弱化灰字**，不能和名字并排——并排会读成
 * 「这个成员叫 claude-sonnet-4.5」。
 *
 * 归属那一档刻意只有两种可能：`declared` 或 `self`。openapi 把
 * `provider_verified` 写成恒为 false 并解释了原因——平台没有能力验证外部 AI
 * 真在用它自称的模型。所以「平台已验证」这个标在这条路径上**永远不出现**，
 * 而不是「暂时不出现」。
 */
function attributionOf(participant: LuluParticipant | undefined) {
  if (!participant?.declared_model) return null
  return participant.provider_verified
    ? ('lulu_workbench.attribution.platform' as const)
    : ('lulu_workbench.attribution.self' as const)
}

/**
 * 页签内容。**读不到与真的没有分开画**——见 LuluViewProps.team 那段注释。
 */
function SidecarBody({
  tab,
  team,
  board,
  decisions,
  bound,
  teamCandidates,
  people,
  invitablePeople,
  onInvitePerson,
  onRemovePerson,
  onJoinTeam,
  plan,
  onRetry,
  link,
  now,
  onAnswerDecision,
  onRecoverSubrun,
  onCancelSubrun,
  onStopRound,
  paused,
  onPause,
  onResume,
  models,
  onUndoTask,
}: {
  onUndoTask?: (taskId: string) => Promise<void>
  tab: SidecarTab
  team: readonly RosterMember[] | null | undefined
  board: readonly WorkItemNode[] | null | undefined
  decisions: readonly PendingDecision[] | null | undefined
  onRecoverSubrun?: (subrunId: string, kind: SubFailExitKind, adapter?: string) => Promise<void>
  onCancelSubrun?: (subrunId: string) => Promise<void>
  onStopRound?: (scope: 'all' | 'turn') => Promise<{ readonly cancelledItems: number }>
  /** 这条话头暂停着没有（agent_conversations.paused_at）。 */
  paused?: boolean
  /** 暂停 / 继续这条话头（POST …/pause、…/resume）。不给就不出现那颗按钮。 */
  onPause?: () => Promise<void>
  onResume?: () => Promise<void>
  models: readonly ModelOption[] | null
  bound: boolean
  link: LinkState
  /** 库里还没进这条会话的那几位。设计稿 teamEmpty 的「先加一位试试」。 */
  teamCandidates?: readonly TeamMember[]
  people?: readonly HumanMember[]
  /** 「一起管」里还没进这条会话的人（L-58）。 */
  invitablePeople?: readonly InvitablePerson[]
  onInvitePerson?: (userId: string) => Promise<void>
  onRemovePerson?: (userId: string) => Promise<void>
  onJoinTeam?: (agentProfileId: string) => Promise<void>
  plan?: CurrentPlan | null
  /** 读不到时那颗「重试」。不给就不渲染按钮——不做一颗点不动的。 */
  onRetry?: () => void
  /** 「此刻」由调用方给，不在组件里 new Date()——那样用例没法验过期。 */
  now: Date
  onAnswerDecision?: (decisionId: string, approve: boolean) => Promise<void>
}) {
  if (tab === 'team') {
    if (team === undefined) return null
    if (team === null) return <Unreachable what={SIDECAR_TAB_COPY.team} onRetry={onRetry} />
    return (
      <TeamPanel
        members={team}
        link={link}
        bound={bound}
        candidates={teamCandidates}
        people={people}
        invitable={invitablePeople}
        onInvitePerson={onInvitePerson}
        onRemovePerson={onRemovePerson}
        onJoinTeam={onJoinTeam}
        plan={plan}
      />
    )
  }
  if (tab === 'board') {
    if (board === undefined) return null
    if (board === null) return <Unreachable what={SIDECAR_TAB_COPY.board} onRetry={onRetry} />
    return (
      <RunBoardPanel
        items={board}
        team={team ?? null}
        link={link}
        onCancelSubrun={onCancelSubrun}
        onStopRound={onStopRound}
        paused={paused}
        onPause={onPause}
        onResume={onResume}
        onRecoverSubrun={onRecoverSubrun}
        onUndo={onUndoTask}
        swapCandidates={models}
      />
    )
  }
  if (tab === 'decisions') {
    // null 是读不到，[] 是真的没有要你决定的事。两句话不一样。
    if (decisions === undefined) return null
    if (decisions === null) {
      return <Unreachable what={SIDECAR_TAB_COPY.decisions} onRetry={onRetry} />
    }
    return <DecisionsPanel decisions={decisions} now={now} onAnswer={onAnswerDecision} />
  }
  // 证据：从工作板上的子 Agent 反查。board 读不到时说读不到，
  // 而不是说「还没有产出」——后者是一句关于事实的断言。
  if (board === undefined) return null
  if (board === null) return <Unreachable what={SIDECAR_TAB_COPY.evidence} onRetry={onRetry} />
  return <EvidencePanel evidence={evidenceFromBoard(board)} workItemId={null} />
}

/**
 * 从工作板推出证据行。
 *
 * 子 Agent 的产出**挂在父工作项上**，所以这里的每一行都带着
 * `workItemId`——设计稿 S03 那句「可由 work_item 反查」的落点。
 */
function evidenceFromBoard(board: readonly WorkItemNode[]): readonly Evidence[] {
  const out: Evidence[] = []
  for (const item of board) {
    for (const run of item.subRuns) {
      const label = resolveDisplayName(run)
      if (label === null) continue
      out.push({ id: run.id, workItemId: item.id, subAgentId: run.id, label })
    }
  }
  return out
}

// Unreachable：这一块**接上了，只是这一次没读到**。
//
// 上一版这里渲染的是「这一块还没接上后端」，而同一段代码的注释
// 写的是「null 是读不到」——注释说的是一件事，屏上说的是另一件。
// 两句话把人指向相反的方向：说「没接上」，人会去查接线（而接线是好的）；
// 说「读不到」，人才会去看这一次请求。
//
// 设计稿 Z10 把这条规矩写死在项目列表上（读失败与真的没有，两句话不同），
// 侧栏这几块是同一条规矩。
function Unreachable({ what, onRetry }: { what: VarlessCopyId; onRetry?: () => void }) {
  return (
    <div data-testid="lulu-sidecar-unreachable">
      <p className="text-npc-caption text-npc-lulu-warn">
        <T id="lulu_workbench.gap.unreachable" vars={{ what: t(what) }} />
      </p>
      {onRetry === undefined ? null : (
        <button
          type="button"
          data-testid="lulu-sidecar-retry"
          onClick={onRetry}
          className="mt-2 h-[30px] rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-3 text-npc-caption text-npc-lulu-t2" /* npc-token-allow: 设计稿桌面按钮高 30px，整站 token 无此档 */
        >
          <T id="lulu_workbench.launch.retry" />
        </button>
      )}
    </div>
  )
}

export function MessageList({
  messages,
  participants,
  board,
  thinkingSeconds,
  link,
  onOpenRun,
  onPickStarter,
  decisions,
  onAnswerDecision,
  runSteps,
  onStopRound,
  conversationId,
}: {
  messages: readonly LuluMessage[]
  participants: ReadonlyMap<string, LuluParticipant>
  board: readonly WorkItemNode[] | null | undefined
  /** 等 Lulu 接话已经多少秒；`null` = 没在等。 */
  thinkingSeconds: number | null
  link: LinkState
  onOpenRun: () => void
  /** 点了一条起手式。不给就不渲染那四颗——不做点不动的按钮。 */
  onPickStarter?: (text: string) => void
  decisions?: readonly PendingDecision[] | null
  onAnswerDecision?: (decisionId: string, approve: boolean) => Promise<void>
  /** 这条会话上收到的真实过程步骤。不给就只显示 block 自带的收口摘要。 */
  runSteps?: readonly RunStep[]
  /** 「停下」：叫停正在想的这一轮。不给就不渲染那颗按钮。 */
  onStopRound?: (scope: 'all' | 'turn') => Promise<{ readonly cancelledItems: number }>
  /** 早晨留言那张卡上的动作要知道自己在哪条话头里。 */
  conversationId?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const pinned = useRef(true)
  // 照 Claude Code 那一行「55s · Thinking some more…」：秒数是真的，
  // 短语是过程流里这一轮最新那一句（在想 / 在查 / 在组织回答）。
  const thinkingPhase = thinkingPhaseOf(runSteps, thinkingSeconds, Date.now())
  // 第二版多了 token：这一轮各跳累计烧掉的（过程事件 payload.tokens）。
  // 还没有一跳回来时是 null，那一行不写 0。
  const thinkingTokens = thinkingTokensOf(runSteps, thinkingSeconds, Date.now())

  // 打开 / 关闭 Sidecar 不该把聊天滚回顶部。React 不会因为兄弟节点增减而卸载
  // 这个组件，但**只要有人哪天把它换成条件渲染，滚动位置就会丢**——
  // 所以这里把「贴不贴底」记下来，让重挂之后至少回到正确的一端。
  // 「下面有新消息」。
  //
  // 2026-09-08 arron：「新消息来了右边对话框应该会自动到最新消息，
  // 现在对话空停了不动」。贴着底（24px 以内）时新消息会把列表带到底；
  // 而任何一张卡撑高、任何一次布局抖动都会把人「解开」，之后新消息静静
  // 落在视野外，没有任何提示——这与 Slack / Claude Code 那颗
  // 「↓ 新消息」是同一件事。点它回到底，回到底它就消失。
  const [newBelow, setNewBelow] = useState(false)
  const seenCount = useRef(messages.length)
  const wasWaiting = useRef(thinkingSeconds !== null)
  const jumpToBottom = useCallback(() => {
    const el = ref.current
    if (el) el.scrollTop = el.scrollHeight
    pinned.current = true
    setNewBelow(false)
  }, [])

  useEffect(() => {
    const grew = messages.length > seenCount.current
    seenCount.current = messages.length
    const el = ref.current
    if (!el) return
    if (pinned.current) {
      el.scrollTop = el.scrollHeight
      return
    }
    if (grew) setNewBelow(true)
  }, [messages.length])

  // 自己刚发了话（开始等回音那一刻）：不管刚才滚到哪，都回到底——
  // 他要看的是自己那句和接下来的回答，不是三屏之前的旧话。
  useEffect(() => {
    const waiting = thinkingSeconds !== null
    if (waiting && !wasWaiting.current) jumpToBottom()
    wasWaiting.current = waiting
  }, [thinkingSeconds, jumpToBottom])

  const hasRun = board?.some((item) => item.subRuns.length > 0) === true

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
    <div
      ref={ref}
      data-testid="lulu-messages"
      onScroll={(event) => {
        const el = event.currentTarget
        pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24
        if (pinned.current) setNewBelow(false)
      }}
      // 隐藏轨道但保留可滚：390 宽下那条轨道要占掉可读宽度（sidebar.tsx 同做法）
      // relative 不是装饰：卡片里「复制」按钮的 sr-only 标签是 absolute 定位，
      // 这层不定位的话它们的包含块在滚动区外面，2 万多像素的列表把整页撑出
      // 一大段空白（2026-09-08 生产实测 #root.scrollHeight 17934 → 1129）。
      className="npc-scrollbar-hidden relative min-h-0 flex-1 overflow-y-auto px-4 py-3"
    >
      {messages.length === 0 && !hasRun ? (
        <div data-testid="lulu-empty" className="mx-auto flex w-full max-w-[880px] flex-col px-6 py-7 max-[720px]:px-4">
          {' '}
          {/* npc-token-allow: after-v4-taste workspace max ~960 with pad */}
          <div className="mb-7 flex flex-col items-start pt-7">
            <div className="lu-wordmark" data-testid="lulu-wordmark">
              Lulu
            </div>
            <h2 className="lu-hero-title mb-3">
              <T id="lulu_workbench.empty.title" />
            </h2>
            <p className="lu-hero-sub">
              <T id="lulu_workbench.empty.desc" />
            </p>
          </div>
          {/*
            Soft Structuralism B · 1+3 bento。四条起手式仍是按钮：点一条填进输入框。
            第一条 featured（珊瑚 soft tint），其余三列。
          */}
          {onPickStarter === undefined ? null : (
            <div data-testid="lulu-empty-starters" className="lu-bento">
              {EMPTY_STARTERS.slice(0, 1).map(([titleId, descId]) => (
                <button
                  key={titleId}
                  type="button"
                  data-testid="lulu-empty-starter"
                  data-featured="yes"
                  onClick={() => onPickStarter(t(titleId))}
                  className="lu-bento-featured"
                >
                  <span className="min-w-0 flex-1">
                    <span className="lu-bento-t block">
                      <T id={titleId} />
                    </span>
                    <span className="lu-bento-d block">
                      <T id={descId} />
                    </span>
                  </span>
                </button>
              ))}
              <div className="lu-bento-row">
                {EMPTY_STARTERS.slice(1).map(([titleId, descId]) => (
                  <button
                    key={titleId}
                    type="button"
                    data-testid="lulu-empty-starter"
                    onClick={() => onPickStarter(t(titleId))}
                    className="lu-bento-card"
                  >
                    <span className="lu-bento-t block">
                      <T id={titleId} />
                    </span>
                    <span className="lu-bento-d block">
                      <T id={descId} />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <p className="lu-caption mt-[18px] text-[11px] opacity-70">
            <T id="lulu_workbench.empty.foot" />
          </p>
        </div>
      ) : (
        <ul className="mx-auto flex max-w-[760px] flex-col gap-3">
          {' '}
          {/* npc-token-allow: 设计稿消息列最大宽 760px，整站 token 无此档 */}
          {messages.map((message) => {
            const who = participants.get(message.participant_id)
            const attribution = attributionOf(who)
            return (
              <li
                key={message.id}
                data-testid="lulu-message"
                data-speaker-kind={who?.kind ?? 'unknown'}
                className="lu-msg text-npc-body-sm"
              >
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  {/* 第一层：名字。解不出来就什么都不显示 —— 一串 uuid 不是「谁」。 */}
                  {who ? (
                    <span data-testid="lulu-message-who" className="font-medium text-npc-lulu-t1">
                      {who.display_name}
                    </span>
                  ) : null}
                  {/* 第三层：模型。等宽、更小、更灰 —— 顺序和字重都是合同的一部分。 */}
                  {who?.declared_model ? (
                    <span
                      data-testid="lulu-message-model"
                      className="min-w-0 truncate font-npc-mono text-npc-meta text-npc-lulu-t4"
                    >
                      {who.declared_model}
                    </span>
                  ) : null}
                  {attribution ? (
                    <span
                      data-testid="lulu-message-attribution"
                      className="shrink-0 text-npc-meta text-npc-lulu-t3"
                    >
                      <T id={attribution} />
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 whitespace-pre-wrap break-words text-npc-lulu-t1">
                  {message.body}
                </p>
                {/* 这条消息带走的文件。**紧贴正文**，在过程卡与结果卡之前——
                    它们是这句话的一部分（「照着这张截图改」里的那张截图），
                    而下面那些卡是这句话**之后**发生的事。
                    `?? []` 是防御性的一档：契约上 attachments 恒在，
                    而 TS 那侧它是可选的，缺了这一档翻页缓存里一条老消息
                    就能把整屏打崩。 */}
                <MessageAttachments items={message.attachments ?? []} />
                {/* 工作过程与结果总结。**认不出的块整块跳过、显示 body**
                    （渲染合同见 blocks.go 顶部），所以上面那段永远在。
                    整块实现在 runcards.tsx —— 这里只有这一行，
                    好让它与别的分支容易集成。 */}
                <RunBlocks
                  blocks={message.blocks}
                  liveSteps={runSteps}
                  messageBody={message.body}
                  receiptSource={message.receipt?.source}
                  trustedPlatformAction={trustedPlatformActionOf(message.receipt)}
                  streamDown={link === 'down'}
                  conversationId={conversationId}
                  decisions={decisions}
                  onAnswerDecision={onAnswerDecision}
                  onAskLulu={onPickStarter}
                />
              </li>
            )
          })}
          {hasRun && board ? (
            <li>
              <WorkflowSummaryCard items={board} link={link} onOpen={onOpenRun} />
            </li>
          ) : null}
          {/*
            **等回话的那几秒，屏幕上要有东西。**

            在这之前那段时间界面完全静止：awaitingReply 这个状态是有的，
            但它只用来把轮询调快，一处都没传给界面。
            arron 2026-08-29：「这个回答还是有问题，没得响应时间状态」。

            秒数是**真的**（从他按下发送那一刻算起），不是进度条：
            这条路上没有人知道「还剩多少」，画一个匀速走的条是编一个数，
            而它走满了还没回来的时候比什么都不显示更让人不安。
          */}
          {thinkingSeconds === null ? null : (
            <li data-testid="lulu-thinking" className="text-npc-body-sm text-npc-lulu-t3">
              <span
                aria-hidden="true"
                className="mr-2 inline-block size-[6px] animate-npc-pulse rounded-full bg-npc-lulu-t3 align-middle" /* npc-token-allow: 与会话行同一档 6px 状态点 */
              />
              {thinkingPhase === null ? (
                <T id="lulu_workbench.chat.thinking" vars={{ seconds: String(thinkingSeconds) }} />
              ) : thinkingTokens === null ? (
                <span data-testid="lulu-thinking-phase">
                  <T
                    id="lulu_workbench.chat.thinking_phase"
                    vars={{ seconds: String(thinkingSeconds), phase: thinkingPhase }}
                  />
                </span>
              ) : (
                <span data-testid="lulu-thinking-phase" data-tokens={String(thinkingTokens)}>
                  <T
                    id="lulu_workbench.chat.thinking_tokens"
                    vars={{
                      seconds: String(thinkingSeconds),
                      tokens: formatTokens(thinkingTokens),
                      phase: thinkingPhase,
                    }}
                  />
                </span>
              )}
              {/*
                「停下」：与 Claude Code 的 Esc 同一件事。走的是「停下这一轮」那条
                （cancel）：工作包收口是内核的事，正在想的那句由回话循环叫停，
                随后 Lulu 说「停下了」。没给回调就不渲染——不做点不动的按钮。
              */}
              {onStopRound ? (
                <button
                  type="button"
                  data-testid="lulu-chat-stop"
                  onClick={() => {
                    // 等回话那一行的「停下」只停这一轮：他这句话之后派出来的，
                    // 与 Esc 同一档。工作板那颗才是「全停」。
                    void onStopRound('turn').catch(() => undefined)
                  }}
                  className="ml-3 rounded-npc-chip border border-npc-lulu-line-1 px-2 py-0.5 text-npc-meta text-npc-lulu-t2 transition-colors hover:bg-npc-lulu-bg-2"
                >
                  <T id="lulu_workbench.chat.stop" />
                </button>
              ) : null}
            </li>
          )}
        </ul>
      )}
    </div>
    {newBelow ? (
      <button
        type="button"
        data-testid="lulu-new-below"
        onClick={jumpToBottom}
        className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-npc-chip border border-npc-lulu-line-1 bg-npc-lulu-bg-2 px-3 py-1 text-npc-caption text-npc-lulu-t2 shadow-npc-popover"
      >
        <T id="lulu_workbench.chat.new_below" />
        <span aria-hidden="true" className="ml-1">↓</span>
      </button>
    ) : null}
    </div>
  )
}

function Sidecar({
  decisions,
  now,
  onRetry,
  teamCandidates,
  people,
  invitablePeople,
  onInvitePerson,
  onRemovePerson,
  onJoinTeam,
  plan,
  onAnswerDecision,
  onRecoverSubrun,
  onCancelSubrun,
  onStopRound,
  paused,
  onPause,
  onResume,
  models,
  tab,
  team,
  board,
  bound,
  link,
  mode,
  width,
  onSelectTab,
  onClose,
  onResize,
  onUndoTask,
}: {
  onUndoTask?: (taskId: string) => Promise<void>
  decisions: readonly PendingDecision[] | null | undefined
  now: Date
  onRetry?: () => void
  teamCandidates?: readonly TeamMember[]
  people?: readonly HumanMember[]
  /** 「一起管」里还没进这条会话的人（L-58）。 */
  invitablePeople?: readonly InvitablePerson[]
  onInvitePerson?: (userId: string) => Promise<void>
  onRemovePerson?: (userId: string) => Promise<void>
  onJoinTeam?: (agentProfileId: string) => Promise<void>
  plan?: CurrentPlan | null
  onAnswerDecision?: (decisionId: string, approve: boolean) => Promise<void>
  onRecoverSubrun?: (subrunId: string, kind: SubFailExitKind, adapter?: string) => Promise<void>
  onCancelSubrun?: (subrunId: string) => Promise<void>
  onStopRound?: (scope: 'all' | 'turn') => Promise<{ readonly cancelledItems: number }>
  /** 这条话头暂停着没有（agent_conversations.paused_at）。 */
  paused?: boolean
  /** 暂停 / 继续这条话头（POST …/pause、…/resume）。不给就不出现那颗按钮。 */
  onPause?: () => Promise<void>
  onResume?: () => Promise<void>
  models: readonly ModelOption[] | null
  tab: SidecarTab
  team: readonly RosterMember[] | null | undefined
  board: readonly WorkItemNode[] | null | undefined
  bound: boolean
  link: LinkState
  mode: SidecarMode
  width: number
  onSelectTab: (tab: SidecarTab) => void
  onClose: () => void
  onResize: (width: number) => void
}) {
  const sheet = mode === 'sheet'
  return (
    <aside
      data-testid="lulu-sidecar"
      data-mode={mode}
      style={sheet ? undefined : { width, flex: `0 0 ${width}px` }}
      className={
        sheet
          ? // 手机：全屏层。390px 的屏摆不下两列，硬摆会把聊天挤出屏幕——
            // 那比盖住更糟，盖住至少还看得见有东西盖着，挤出去是直接消失。
            'fixed inset-0 z-40 flex min-h-0 flex-col bg-npc-lulu-bg-1'
          : 'relative flex min-h-0 flex-col border-l border-npc-lulu-line-2 bg-npc-lulu-bg-1'
      }
    >
      {/* 拖拽把手。它是 absolute，但**相对的是 Sidecar 自己**（上面那个 relative）， */}
      {/* 不是视口——所以它不会盖到聊天列上，只压在两条轨道的接缝上。 */}
      {/* 手机上没有它：全屏层没有「和谁并排」这回事，也就没有可拖的接缝。 */}
      {sheet ? null : (
        <div
          data-testid="lulu-sidecar-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label={t('lulu_workbench.sidecar.width_hint')}
          title={t('lulu_workbench.sidecar.width_hint')}
          onPointerDown={(event) => {
            const startX = event.clientX
            const startWidth = width
            const move = (e: PointerEvent) => onResize(startWidth - (e.clientX - startX))
            const up = () => {
              window.removeEventListener('pointermove', move)
              window.removeEventListener('pointerup', up)
            }
            window.addEventListener('pointermove', move)
            window.addEventListener('pointerup', up)
          }}
          className="absolute -left-[3px] top-0 bottom-0 z-[2] w-[6px] cursor-col-resize" /* npc-token-allow: 设计稿拖拽把手 6px 宽、左偏 3px，整站 token 无此档 */
        />
      )}

      <header className="shrink-0 border-b border-npc-lulu-line-1 px-3 py-2.5">
        <div className="flex items-start gap-2">
          <h2 className="min-w-0 flex-1 font-npc-display text-npc-section text-npc-lulu-t1">
            <T id="lulu_workbench.sidecar.title_work" />
          </h2>
          {sheet ? null : (
            <span
              data-testid="lulu-sidecar-width"
              className="shrink-0 font-npc-mono text-npc-meta text-npc-lulu-t4"
            >
              {width}
            </span>
          )}
          <button
            type="button"
            data-testid="lulu-sidecar-close"
            onClick={onClose}
            className={
              sheet
                ? // 手机上这是唯一一条回到对话的路，所以它够得着（≥40px）也说得清。
                  'flex h-10 shrink-0 items-center rounded-npc-chip border border-npc-lulu-line-2 px-3 text-npc-caption text-npc-lulu-t1'
                : 'shrink-0 rounded-npc-chip border border-npc-lulu-line-2 px-2 py-0.5 text-npc-meta text-npc-lulu-t2'
            }
          >
            <T
              id={sheet ? 'lulu_workbench.sidecar.back_to_chat' : 'lulu_workbench.sidecar.close'}
            />
          </button>
        </div>
      </header>

      <div
        role="tablist"
        aria-label={t('lulu_workbench.sidecar.title_work')}
        className="flex shrink-0 gap-1 border-b border-npc-lulu-line-1 px-2"
      >
        {SIDECAR_TABS.map((candidate) => (
          <button
            key={candidate}
            type="button"
            role="tab"
            aria-selected={candidate === tab}
            data-testid={`lulu-sidecar-tab-${candidate}`}
            onClick={() => onSelectTab(candidate)}
            className={[
              'px-2 py-1.5 text-npc-caption',
              candidate === tab ? 'text-npc-lulu-t1' : 'text-npc-lulu-t3',
            ].join(' ')}
          >
            <T id={SIDECAR_TAB_COPY[candidate]} />
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        data-testid="lulu-sidecar-panel"
        /* 内容区锚顶：列表是从第一行往下读的，不是聊天那种锚底。
           relative 与消息列表同一个理由：绝对定位的子元素要以滚动区为包含块。 */
        className="relative min-h-0 flex-1 overflow-y-auto px-3 py-3"
      >
        <SidecarBody
          tab={tab}
          onRetry={onRetry}
          teamCandidates={teamCandidates}
          people={people}
          invitablePeople={invitablePeople}
          onInvitePerson={onInvitePerson}
          onRemovePerson={onRemovePerson}
          onJoinTeam={onJoinTeam}
          plan={plan}
          team={team}
          board={board}
          decisions={decisions}
          bound={bound}
          link={link}
          now={now}
          onAnswerDecision={onAnswerDecision}
          onRecoverSubrun={onRecoverSubrun}
          onUndoTask={onUndoTask}
          onCancelSubrun={onCancelSubrun}
          onStopRound={onStopRound}
          paused={paused}
          onPause={onPause}
          onResume={onResume}
          models={models ?? null}
        />
      </div>

      {sheet ? null : (
        <footer className="shrink-0 border-t border-npc-lulu-line-1 px-3 py-2">
          <p className="text-npc-meta text-npc-lulu-t3">
            <T id="lulu_workbench.sidecar.keep_state" />
          </p>
        </footer>
      )}
    </aside>
  )
}

export { RAIL_ICON_WIDTH }
